// -*- coding: utf-8 -*-
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getApiBase, fetchJson } from '../../apiBase';

function api() {
  const base = `${getApiBase()}/admin`;
  return {
    classes: (schoolId) => `${base}/classes/${encodeURIComponent(schoolId)}`,
    aggregate: (schoolId, classId) => {
      const usp = new URLSearchParams();
      if (classId) usp.set('class_id', classId);
      return `${base}/school-exam-results/${encodeURIComponent(schoolId)}/aggregate?${usp.toString()}`;
    },
  };
}

// Transform backend items into a flat structure for table rendering
function buildRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it, idx) => {
    const lessonsArray = Object.entries(it.lessons || {}).map(([code, v]) => ({
      lesson_code: code,
      lesson_label: v.lesson_name || code,
      avg: typeof v.avg === 'number' ? v.avg : null,
    })).sort((a,b)=> a.lesson_label.localeCompare(b.lesson_label,'tr-TR'));
    // Flatten and normalize per-student row
    return {
      rank: idx + 1,
      student_id: it.student_id,
      full_name: `${it.name || ''} ${it.surname || ''}`.trim(),
      class_name: it.class_name || '',
      overall_avg: it.overall_avg == null ? null : Number(it.overall_avg),
      lessons: lessonsArray,
      lesson_count: it.lesson_count || lessonsArray.length
    };
  });
}

export default function SchoolExamResultsRankingPage({ schoolId, onError }) {
  const apis = useMemo(() => api(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [loading, setLoading] = useState(false);
  const [rawItems, setRawItems] = useState([]);

  // Load classes
  useEffect(() => {
    let active = true;
    if (!schoolId) return;
    setLoadingClasses(true);
    (async () => {
      try {
        const res = await fetchJson(apis.classes(schoolId));
        if (res.ok) {
          const data = await res.json();
          const classList = Array.isArray(data) ? data.filter(c => c && c.id) : [];
          classList.sort((a,b)=> String(a.name || '').localeCompare(String(b.name || ''),'tr-TR'));
          if (active) setClasses(classList);
        }
      } catch (_) { /* ignore */ }
      finally { if (active) setLoadingClasses(false); }
    })();
    return () => { active = false; };
  }, [schoolId, apis]);

  const loadAggregate = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await fetchJson(apis.aggregate(schoolId, selectedClass?.id || undefined));
      if (!res.ok) throw new Error('fail');
      const data = await res.json();
      setRawItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      onError && onError('Ağırlıklı ortalamalar getirilemedi');
      setRawItems([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClass, apis, onError]);

  useEffect(() => { loadAggregate(); }, [loadAggregate]);

  const rows = useMemo(() => buildRows(rawItems), [rawItems]);

  const headerBg = theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main;
  const headerFg = theme.palette.getContrastText(headerBg);

  return (
    <Box>
      {/* Top bar */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight:600 }}>Okul Sınav Ortalamaları</Typography>
          <Typography variant="caption" color="text.secondary">Öğrenciler ders bazlı ortalamalarının genel ortalaması ile sıralanır</Typography>
        </Box>
        <Tooltip title="Yenile"><span><IconButton onClick={loadAggregate} disabled={loading}><RefreshIcon /></IconButton></span></Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{ display:'flex', flexWrap:'wrap', gap:2, mb:3 }}>
        <Autocomplete
          size="small"
          sx={{ minWidth:240 }}
          options={classes}
          getOptionLabel={(opt)=> opt.name || ''}
          value={selectedClass || null}
          onChange={(_,v)=> setSelectedClass(v || null)}
          loading={loadingClasses}
          renderInput={(params) => (
            <TextField {...params} label="Şube (opsiyonel)" placeholder="Seç / yaz" InputProps={{ ...params.InputProps, endAdornment:(<>{loadingClasses ? <Skeleton variant="circular" width={16} height={16} /> : null}{params.InputProps.endAdornment}</>) }} />
          )}
          clearOnEscape
        />
      </Box>

      <Paper sx={{ p:0, borderRadius:2 }}>
        {loading ? (
          <Box sx={{ p:2 }}>
            {[...Array(6)].map((_,i)=>(<Skeleton key={i} variant="rectangular" height={isMobile?96:44} sx={{ mb:1.25, borderRadius:1 }} />))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py:4, textAlign:'center', color:'text.secondary' }}><Typography variant="body2">Henüz veri yok</Typography></Box>
        ) : isMobile ? (
          <Box sx={{ maxHeight:520, overflowY:'auto', p:1 }}>
            {rows.map((r, idx) => (
              <Box key={r.student_id} sx={{ mb:1.5, p:1.25, border:'1px solid', borderColor:'divider', borderRadius:1.5, bgcolor: idx % 2 === 0 ? 'background.default' : 'background.paper' }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight:600 }}>#{r.rank} {r.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.class_name || '—'} • Ders Sayısı: {r.lesson_count}</Typography>
                  </Box>
                  <Box sx={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:0.5 }}>
                    <Chip label={`Genel Ort: ${r.overall_avg == null ? '-' : r.overall_avg.toFixed(2)}`} size="small" color="primary" />
                    <Button size="small" variant="outlined" onClick={() => window.open(`/admin/student360?student_id=${encodeURIComponent(r.student_id)}`, '_blank')}>360</Button>
                  </Box>
                </Box>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.75 }}>
                  {r.lessons.map(lesson => (
                    <Chip key={lesson.lesson_code} label={`${lesson.lesson_label}: ${lesson.avg == null ? '-' : lesson.avg.toFixed(2)}`} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box>
            <Box sx={{
              display:'grid',
              gridTemplateColumns:'60px minmax(200px,1fr) 90px 110px 120px',
              columnGap:2,
              px:2,
              py:1.2,
              bgcolor: headerBg,
              color: headerFg,
              borderTopLeftRadius: theme.shape.borderRadius,
              borderTopRightRadius: theme.shape.borderRadius,
              alignItems:'center'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight:600 }}>Sıra</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight:600 }}>Öğrenci</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight:600, textAlign:'center' }}>Ders Sayısı</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight:600, textAlign:'center' }}>Genel Ortalama</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight:600, textAlign:'center' }}>İşlemler</Typography>
            </Box>
            <Box sx={{ maxHeight:520, overflowY:'auto' }}>
              {rows.map((r, idx) => (
                <Box key={r.student_id} sx={{
                  display:'grid',
                  gridTemplateColumns:'60px minmax(200px,1fr) 90px 110px 120px',
                  columnGap:2,
                  px:2,
                  py:1.2,
                  bgcolor: idx % 2 === 0 ? 'background.default' : 'background.paper',
                  borderBottom:'1px solid',
                  borderBottomColor:'divider',
                  alignItems:'center'
                }}>
                  <Typography variant="body2" sx={{ fontWeight:600 }}>#{r.rank}</Typography>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight:500 }}>{r.full_name} {r.class_name ? `• ${r.class_name}` : ''}</Typography>
                    <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, mt:0.5 }}>
                      {r.lessons.map(lesson => (
                        <Chip key={lesson.lesson_code} label={`${lesson.lesson_label}: ${lesson.avg == null ? '-' : lesson.avg.toFixed(2)}`} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight:600, textAlign:'center' }}>{r.lesson_count}</Typography>
                  <Typography variant="body2" sx={{ fontWeight:600, textAlign:'center' }}>{r.overall_avg == null ? '-' : r.overall_avg.toFixed(2)}</Typography>
                  <Box sx={{ display:'flex', justifyContent:'center' }}>
                    <Button size="small" variant="outlined" onClick={() => window.open(`/admin/student360?student_id=${encodeURIComponent(r.student_id)}`, '_blank')}>öğrenci360</Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
      <Divider sx={{ mt:3, mb:1 }} />
      <Typography variant="caption" color="text.secondary">Genel ortalama, öğrencinin ders bazlı ortalamalarının aritmetik ortalamasıdır. Ders seçilmezse tüm öğrenciler listelenir; ders notu olmayan öğrenciler en altta görünür.</Typography>
    </Box>
  );
}
